import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { taskApi } from '../services/taskApi';
import { ItemCreate, ItemUpdate, ListItemsParams } from '../types';
import type { CrossBoardParams } from '../utils/crossBoardParams';
import { BOARD_SUMMARIES_QUERY_KEY } from './useBoardSummaries';
import { dashboardKeys } from './useDashboard';
import { GROUPS_QUERY_KEY } from './useGroups';

export const TASKS_QUERY_KEY = 'tasks';
export const MY_TASKS_QUERY_KEY = 'my-tasks';
export const ORPHANED_TASKS_QUERY_KEY = 'orphaned-tasks';

export function useTasks(
  boardId: string,
  params?: ListItemsParams,
  options?: {
    enabled?: boolean;
    /**
     * Hold the current rows while the next result loads — for the SERVER-PAGED
     * callers only (the board list view and BoardSection). Their window, search
     * and filters are all in the query key, so without it the table blanks on
     * every page step and every keystroke (ui-list-pages.md § 2).
     *
     * Opt-in rather than default because the other callers of this hook fetch a
     * board's COMPLETE set and switch `boardId` (TaskBoard, TaskDetailContent's
     * parent picker, BoardSettingsPage): there, holding previous data renders the
     * previous board's tasks under the new board's heading until the fetch lands.
     */
    keepPreviousData?: boolean;
  },
) {
  const enabled = options?.enabled ?? true;
  return useQuery({
    queryKey: [TASKS_QUERY_KEY, boardId, params],
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    queryFn: () => taskApi.listByBoard(boardId, params),
    enabled: enabled && !!boardId,
    staleTime: 10_000,
  });
}

export function useMyTasks(params?: {
  statuses?: string[];
  priorities?: string[];
  board_id?: string;
  due_date_from?: string;
  due_date_to?: string;
  search?: string;
  sprint_id?: string;
  tags?: string;
  tags_mode?: 'any' | 'all';
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: [MY_TASKS_QUERY_KEY, params],
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => taskApi.listMyItems(params),
  });
}

export const CROSS_BOARD_TASKS_QUERY_KEY = 'cross-board-tasks';

export function useCrossBoardTasks(params?: CrossBoardParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [CROSS_BOARD_TASKS_QUERY_KEY, params],
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => taskApi.listCrossBoard(params),
    enabled: options?.enabled ?? true,
  });
}

export function useOrphanedTasks(params?: {
  search?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: [ORPHANED_TASKS_QUERY_KEY, params],
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => taskApi.listOrphaned(params),
  });
}

export function useOrphanedTasksCount() {
  return useQuery({
    queryKey: [ORPHANED_TASKS_QUERY_KEY, 'count'],
    queryFn: () => taskApi.countOrphaned(),
  });
}

export function useTask(
  id: string,
  options?: {
    includeSubitems?: boolean;
    includeAssignees?: boolean;
    includeWatchers?: boolean;
  }
) {
  return useQuery({
    queryKey: [TASKS_QUERY_KEY, id, options],
    queryFn: () => taskApi.get(id, options),
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boardId, data }: { boardId: string | null; data: ItemCreate }) =>
      taskApi.create(boardId, data),
    onSuccess: (_, { boardId }) => {
      if (boardId) {
        queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY, boardId] });
        queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY, boardId] });
        queryClient.invalidateQueries({ queryKey: ['tasks', 'okr-tree', boardId] });
      }
      queryClient.invalidateQueries({ queryKey: [MY_TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CROSS_BOARD_TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BOARD_SUMMARIES_QUERY_KEY] });
      // Invalidate sprint items queries so sprint kanban view reflects new items
      queryClient.invalidateQueries({ queryKey: ['sprints', 'detail'] });
      // Invalidate dashboard so empty state transitions to full dashboard
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ItemUpdate }) =>
      taskApi.update(id, data),
    onSuccess: (_, { data }) => {
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MY_TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CROSS_BOARD_TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BOARD_SUMMARIES_QUERY_KEY] });
      // Invalidate sprint items queries for any item update (title, status, priority, etc.)
      // This ensures the sprint kanban view reflects changes
      queryClient.invalidateQueries({ queryKey: ['sprints', 'detail'] });
      // Invalidate backlog queries so sprint view backlog column reflects changes
      queryClient.invalidateQueries({ queryKey: ['sprints', 'backlog'] });
      // If sprint_id was changed, also invalidate sprint lists
      if ('sprint_id' in data) {
        queryClient.invalidateQueries({ queryKey: ['sprints', 'list'] });
      }
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => taskApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MY_TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CROSS_BOARD_TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ORPHANED_TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BOARD_SUMMARIES_QUERY_KEY] });
    },
  });
}

/**
 * Place an item directly before another item in the ordering.
 * This is the primary reorder operation for drag-and-drop.
 */
export function usePlaceBefore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, targetId }: { id: string; targetId: string }) =>
      taskApi.placeBefore(id, targetId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['sprints', 'detail'] });
    },
  });
}

/**
 * Place an item directly after another item in the ordering.
 * This is the primary reorder operation for drag-and-drop.
 */
export function usePlaceAfter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, targetId }: { id: string; targetId: string }) =>
      taskApi.placeAfter(id, targetId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['sprints', 'detail'] });
    },
  });
}

/**
 * Place an item at the end of its sibling list.
 * Used when dropping on an empty area or at the end of a list/column.
 */
export function usePlaceAtEnd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => taskApi.placeAtEnd(id),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['sprints', 'detail'] });
    },
  });
}

export function useMoveToParent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      parentItemId,
    }: {
      id: string;
      parentItemId: string | null;
    }) => taskApi.moveToParent(id, parentItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY] });
    },
  });
}

export function useTaskChildren(taskId: string) {
  return useQuery({
    queryKey: [TASKS_QUERY_KEY, taskId, 'children'],
    queryFn: () => taskApi.getChildren(taskId),
    enabled: !!taskId,
  });
}

export function useSetTaskAssignees() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, userIds }: { taskId: string; userIds: string[] }) =>
      taskApi.setAssignees(taskId, userIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MY_TASKS_QUERY_KEY] });
    },
  });
}

export function useWatchTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => taskApi.watch(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
    },
  });
}

export function useUnwatchTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => taskApi.unwatch(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
    },
  });
}
